/**
 * @author       matthew@zumper.com (Matthew Talamini)
 * @copyright    Copyright (c) 2022, Zumper
 * @description  DesktopView/ListItem content component
 */
/** @jsx jsx */

import { Box, useColorModeValue } from '@chakra-ui/react'
import { jsx } from '@emotion/react'
import { cn } from '@zcode-ui/shadz-ui'
import { selectNow } from '@zcode-ui/web-zumper-global-state'
import { PropertyFeatures } from '@zcode/enums'
import type { Listable } from '@zcode/state-type'
import React from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { getViewingText } from '../../helpers/getViewingText'
import { BedBathInfoMin } from '../BedBathInfoMin'
import { HeaderAndLocationContainer } from '../HeaderAndLocation/HeaderAndLocationContainer'
import { InfoIcon } from '../InfoIcon/InfoIcon'
import { SweetDealPercentContainer } from '../SweetDealPercent/SweetDealPercentContainer'
import { Viewing } from '../Viewing'
import * as styles from './style'

export interface Props {
  entity: Listable
  setShowInfo: React.Dispatch<React.SetStateAction<boolean>>
  showInfo: boolean
}

export const ListItemDesktopViewDetails = ({ entity, setShowInfo, showInfo }: Props) => {
  const location = useLocation()
  const {
    bathroomTxt,
    maxBathrooms,
    bedsBathsRangeTxt,
    bedroomTxt,
    features,
    headerText,
    isBuilding,
    isLargeListCard,
    isShortTermFeed,
    isShortTermAvailability,
    isUnavailable,
    paidPhone,
    pricePerNightText,
    priceTxt,
    tz,
    viewings,
  } = entity

  const ageColor = useColorModeValue('textLightest', 'gray.300')
  const priceColor = useColorModeValue('text', 'white')
  const hasNoPrice = priceTxt === 'N/A' && isUnavailable
  const now = useSelector(selectNow)
  const formattedAgeText = `${entity.getAgeTextMini(now)} ago`
  let priceText
  if (hasNoPrice) {
    priceText = '$ -'
  } else if ((isShortTermFeed || isShortTermAvailability) && pricePerNightText) {
    priceText = pricePerNightText
  } else if (priceTxt === 'N/A' && features?.includes(PropertyFeatures.USES_WAITLIST)) {
    priceText = '$ - Call for pricing'
  } else {
    priceText = priceTxt
  }
  const viewingText = getViewingText(viewings, tz)
  return (
    <div className="ListItemDesktopViewDetails">
      <div css={styles.priceAndIcons}>
        <div css={styles.priceAndViewing}>
          {/* @ts-expect-error */}
          <Box<'div'>
            color={priceColor}
            css={[
              styles.price,
              isUnavailable && styles.unavailablePrice,
              hasNoPrice && styles.noPrice,
            ]}
          >
            <span>{priceText}</span>
            {!isShortTermFeed && <SweetDealPercentContainer listable={entity} />}
          </Box>
          {viewingText && (
            <Viewing
              theme={{
                viewing: cn('flex', 'items-center', 'p-1', 'bg-green-500', 'rounded'),
                viewingText: cn('pl-[6px]', 'text-gray-0', 'text-[13px]', 'leading-none'),
              }}
              viewingText={viewingText}
            />
          )}
        </div>
        <div css={styles.icons}>
          <InfoIcon
            headerText={headerText}
            location={location}
            setShowInfo={setShowInfo}
            showInfo={showInfo}
            size={16}
          />
        </div>
      </div>
      <div css={styles.bedBathAge}>
        <div className={cn('flex', 'items-center')}>
          {!isUnavailable && (
            <BedBathInfoMin
              bathroomTxt={bathroomTxt}
              bathrooms={maxBathrooms}
              bedroomTxt={bedroomTxt}
              bedsBathsRangeTxt={bedsBathsRangeTxt}
              isBuilding={isBuilding}
              styles={cn('mx-[6px]', 'mr-4', 'text-[15px]', 'leading-none', 'text-gray-500')}
            />
          )}
          {isUnavailable && <span css={styles.unavailableText}>Call for info</span>}
        </div>
        <Box<'div'> color={ageColor} fontSize="xs">
          {formattedAgeText}
        </Box>
      </div>
      <HeaderAndLocationContainer
        aria-hidden={showInfo}
        entity={entity}
        isBuilding={isBuilding}
        isLargeCard={isLargeListCard}
        paidPhone={paidPhone}
      />
    </div>
  )
}
